import { useState, useEffect, useCallback } from 'react';
import { envisFindingsApi } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const useDefectStats = (user) => {
  const { getUserOrganization } = useAuth();
  const organizationId = getUserOrganization()?.id;
  
  console.log(`🔒 [useDefectStats] Hook initialized with user:`, {
    username: user?.username,
    permissionScope: user?.permissionScope,
    accessibleNetworks: user?.accessible_networks?.length || 0,
    accessibleSections: user?.accessible_sections?.length || 0,
    organizationId
  });
  
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    validated: 0,
    rejected: 0,
    newDefects: 0,
    inProgress: 0,
    resolved: 0,
    withWorkOrders: 0,
    withoutWorkOrders: 0,
    overdueDefects: 0,
    dueSoonDefects: 0,
    onTimeDefects: 0,
    outOfBoundary: 0,
    inBoundary: 0,
    unresolvedPotholes: 0,
    unresolvedForeignObjects: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const calculateStats = useCallback(async () => {
    if (!organizationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Build parameters for permission-based filtering
      const params = {};
      
      // 🔐 ADD PERMISSION-BASED FILTERING FOR STATS
      if (user?.permissionScope === 'network_section') {
        const accessibleNetworkIds = user.accessible_networks || [];
        const accessibleSectionIds = user.accessible_sections || [];
        
        // Apply OR logic: Include both network and section filtering
        if (accessibleNetworkIds.length > 0) {
          params.network_ids = accessibleNetworkIds.join(',');
        }
        if (accessibleSectionIds.length > 0) {
          params.section_ids = accessibleSectionIds.join(',');
        }
        
        // If no permissions, return empty stats
        if (!accessibleNetworkIds.length && !accessibleSectionIds.length) {
          console.warn(`[useDefectStats] User ${user.username} has no network or section permissions - returning empty stats`);
          setStats({
            total: 0, draft: 0, validated: 0, rejected: 0,
            newDefects: 0, inProgress: 0, resolved: 0,
            withWorkOrders: 0, withoutWorkOrders: 0,
            overdueDefects: 0, dueSoonDefects: 0, onTimeDefects: 0,
            outOfBoundary: 0, inBoundary: 0, unresolvedPotholes: 0,
            unresolvedForeignObjects: 0,
          });
          setLoading(false);
          return;
        }
        
        console.log(`🔒 [useDefectStats] Applying permission-based filtering for stats:`, {
          networkIds: params.network_ids,
          sectionIds: params.section_ids,
          userNetworks: accessibleNetworkIds.length,
          userSections: accessibleSectionIds.length
        });
      }
      
      // Call the new server-side dashboard statistics endpoint
      // Ensure stats reflect only in-boundary records to match dashboard tables
      params.boundary_status = 'in_boundary';

      console.log(`📊 [useDefectStats] Fetching server-side dashboard stats...`, params);
      const response = await envisFindingsApi.getDashboardStats(params);
      
      const serverStats = response.data || {};
      
      // Map server response to expected format
      setStats({
        total: serverStats.total || 0,
        draft: serverStats.draft || 0,
        validated: serverStats.validated || 0,
        rejected: serverStats.rejected || 0,
        newDefects: serverStats.newDefects || 0,
        inProgress: serverStats.inProgress || 0,
        resolved: serverStats.resolved || 0,
        withWorkOrders: serverStats.withWorkOrders || 0,
        withoutWorkOrders: serverStats.withoutWorkOrders || 0,
        overdueDefects: serverStats.overdueDefects || 0,
        dueSoonDefects: serverStats.dueSoonDefects || 0,
        onTimeDefects: serverStats.onTimeDefects || 0,
        outOfBoundary: serverStats.outOfBoundary || 0,
        inBoundary: serverStats.inBoundary || 0,
        unresolvedPotholes: serverStats.unresolvedPotholes || 0,
        unresolvedForeignObjects: serverStats.unresolvedForeignObjects || 0,
      });
      
      console.log(`✅ [useDefectStats] Server-side stats loaded successfully:`, {
        total: serverStats.total,
        unresolvedPotholes: serverStats.unresolvedPotholes,
        overdueDefects: serverStats.overdueDefects
      });
      
    } catch (err) {
      console.error('Failed to calculate defect stats:', err);
      setError('Failed to load statistics');
      // Set empty stats on error to prevent UI breaking
      setStats({
        total: 0,
        draft: 0,
        validated: 0,
        rejected: 0,
        newDefects: 0,
        inProgress: 0,
        resolved: 0,
        withWorkOrders: 0,
        withoutWorkOrders: 0,
        overdueDefects: 0,
        dueSoonDefects: 0,
        onTimeDefects: 0,
        outOfBoundary: 0,
        inBoundary: 0,
        unresolvedPotholes: 0,
        unresolvedForeignObjects: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId, user]);

  useEffect(() => {
    calculateStats();
  }, [calculateStats]);

  return {
    stats,
    loading,
    error,
    refreshStats: calculateStats,
  };
};

export default useDefectStats; 